/**
 * Deals Service
 *
 * Handles deal-related API calls including:
 * - Pitch deck upload and deal creation
 * - Deal listing and retrieval
 * - Chat with deal (RAG)
 * - Deal diligence processing
 */

import { apiClient } from '@/lib/api-client'
import { isMockModeEnabled, getMockSpeed } from '../utils/mockMode'
import type {
  Deal,
  DealUpdateRequest,
  CreateFromPitchDeckResponse,
  ChatRequest,
  ChatResponse,
  ChatStatusResponse,
  ChatHistoryResponse,
  DiligenceStatusResponse,
  DiligenceSummaryResponse,
  DiligenceFindingsResponse,
  StartDiligenceResponse,
  DiligencePhase,
  EmbeddingProcessResponse,
  // Contacts
  DealContactListResponse,
  DealContactCreateRequest,
  DealContactUpdateRequest,
  DealContact,
  // Knowledge
  DealKnowledgeResponse,
  AddLinkRequest,
  DealKnowledgeItem,
  // Events
  DealEventListResponse,
  // Investment Memo
  GetMemoResponse,
  // Comments
  DealComment,
  DealCommentCreate,
  DealCommentUpdate,
  DealCommentStatusUpdate,
  DealCommentListResponse,
  ListCommentsParams,
  // Version History
  DiligenceVersionListResponse,
  DiligenceVersionSnapshot,
} from '../types/deal.types'
import type { DealListResponse } from '@/features/dashboard/types/dashboard.types'

export interface ListDealsParams {
  stage?: string  // Single stage (backend doesn't support multiple)
  fund_id?: string
  lead_partner_id?: string
  sector?: string
  priority?: string
  sort?: string
  limit?: number
  offset?: number
}

export const dealsService = {
  // ============================================
  // Deal CRUD
  // ============================================

  /**
   * List deals with optional filtering
   */
  list: async (params?: ListDealsParams): Promise<DealListResponse> => {
    return apiClient.get<DealListResponse>('/api/v1/deals', { params })
  },

  /**
   * Get a single deal by ID
   */
  getById: async (dealId: string): Promise<Deal> => {
    return apiClient.get<Deal>(`/api/v1/deals/${dealId}`)
  },

  /**
   * Update a deal
   *
   * Supports partial updates - only fields included in data will be updated.
   *
   * @param dealId - Deal ID to update
   * @param data - Partial deal data to update
   * @returns Updated deal
   */
  updateDeal: async (dealId: string, data: DealUpdateRequest): Promise<Deal> => {
    return apiClient.put<Deal>(`/api/v1/deals/${dealId}`, data)
  },

  /**
   * Update deal stage using PATCH endpoint
   *
   * This endpoint auto-triggers Quick Screen when moving to "screening" stage.
   * Use this for stage changes that should trigger analysis.
   *
   * @param dealId - Deal ID to update
   * @param stage - New stage value
   * @param reason - Optional reason for stage change
   * @returns Updated deal
   */
  updateDealStage: async (
    dealId: string,
    stage: string,
    reason?: string
  ): Promise<Deal> => {
    return apiClient.patch<Deal>(`/api/v1/deals/${dealId}/stage`, { stage, reason })
  },

  /**
   * Delete a deal
   *
   * @param dealId - Deal ID to delete
   */
  deleteDeal: async (dealId: string): Promise<void> => {
    return apiClient.delete(`/api/v1/deals/${dealId}`)
  },

  /**
   * Create a deal from a pitch deck file
   *
   * This endpoint:
   * 1. Extracts company/deal information from the pitch deck using AI
   * 2. Creates the deal with extracted data
   * 3. Uploads the pitch deck as a document to the deal's knowledge base
   *
   * @param file - PDF file to upload
   * @returns Created deal with extraction summary
   */
  createFromPitchDeck: async (file: File, companyName?: string): Promise<CreateFromPitchDeckResponse> => {
    const formData = new FormData()
    formData.append('file', file)
    if (companyName) {
      formData.append('company_name', companyName)
    }

    return apiClient.uploadFile<CreateFromPitchDeckResponse>(
      '/api/v1/deals/create-from-pitch-deck',
      formData
    )
  },

  /**
   * Create a deal from a pitch deck URL (e.g., DocSend link)
   *
   * @param url - URL to the pitch deck (DocSend or other)
   * @param email - Email for DocSend email gate (required for DocSend links)
   * @returns Created deal with extraction summary
   */
  createFromPitchDeckUrl: async (url: string, email?: string, companyName?: string): Promise<CreateFromPitchDeckResponse> => {
    // Mock mode: use mock endpoint with fixture data (no API costs)
    if (isMockModeEnabled()) {
      const formData = new FormData()
      formData.append('url', url)
      if (companyName) {
        formData.append('company_name', companyName)
      }
      const speed = getMockSpeed()
      return apiClient.uploadFile<CreateFromPitchDeckResponse>(
        `/api/v1/mock/deals/create-from-url?speed=${speed}`,
        formData
      )
    }

    const formData = new FormData()
    formData.append('url', url)
    if (email) {
      formData.append('email', email)
    }
    if (companyName) {
      formData.append('company_name', companyName)
    }

    return apiClient.uploadFile<CreateFromPitchDeckResponse>(
      '/api/v1/deals/create-from-pitch-deck',
      formData
    )
  },

  // ============================================
  // Chat (RAG)
  // ============================================

  /**
   * Check if deal is ready for chat (embeddings complete)
   */
  getChatStatus: async (dealId: string): Promise<ChatStatusResponse> => {
    return apiClient.get<ChatStatusResponse>(`/api/v1/deals/${dealId}/chat/status`)
  },

  /**
   * Send a chat message and get AI response
   */
  sendChatMessage: async (dealId: string, request: ChatRequest): Promise<ChatResponse> => {
    return apiClient.post<ChatResponse>(`/api/v1/deals/${dealId}/chat`, request)
  },

  /**
   * Get chat history for a conversation
   */
  getChatHistory: async (dealId: string, conversationId: string): Promise<ChatHistoryResponse> => {
    return apiClient.get<ChatHistoryResponse>(
      `/api/v1/deals/${dealId}/chat/history`,
      { params: { conversation_id: conversationId } }
    )
  },

  /**
   * Clear chat history for a conversation
   */
  clearChatHistory: async (dealId: string, conversationId: string): Promise<{ success: boolean; message: string }> => {
    return apiClient.delete<{ success: boolean; message: string }>(
      `/api/v1/deals/${dealId}/chat/history`,
      { params: { conversation_id: conversationId } }
    )
  },

  // ============================================
  // Diligence Processing
  // ============================================

  /**
   * Start diligence analysis (quick_screen or deep_dive)
   */
  startDiligence: async (
    dealId: string,
    phase: DiligencePhase,
    force = false
  ): Promise<StartDiligenceResponse> => {
    return apiClient.post<StartDiligenceResponse>(
      `/api/v1/deals/${dealId}/diligence?phase=${phase}&force=${force}`
    )
  },

  /**
   * Start refresh analysis (lightweight update of existing Quick Screen)
   */
  startRefreshAnalysis: async (
    dealId: string,
    force = false
  ): Promise<StartDiligenceResponse> => {
    return apiClient.post<StartDiligenceResponse>(
      `/api/v1/deals/${dealId}/diligence/refresh?force=${force}`
    )
  },

  /**
   * Get diligence status and results (including IMPACT-X analysis)
   */
  getDiligenceStatus: async (dealId: string): Promise<DiligenceStatusResponse> => {
    return apiClient.get<DiligenceStatusResponse>(`/api/v1/deals/${dealId}/diligence`)
  },

  /**
   * Get diligence summary WITHOUT findings (lightweight ~50KB)
   * Use for initial page load, fetch /findings on-demand
   */
  getDiligenceSummary: async (dealId: string): Promise<DiligenceSummaryResponse> => {
    return apiClient.get<DiligenceSummaryResponse>(`/api/v1/deals/${dealId}/diligence/summary`)
  },

  /**
   * Get all 7 dimension findings (~350KB)
   * Fetched on-demand when user expands dimension cards
   */
  getDiligenceFindings: async (dealId: string): Promise<DiligenceFindingsResponse> => {
    return apiClient.get<DiligenceFindingsResponse>(`/api/v1/deals/${dealId}/diligence/findings`)
  },

  /**
   * Cancel ongoing Deep Dive analysis
   */
  cancelDeepDive: async (dealId: string): Promise<void> => {
    return apiClient.post<void>(`/api/v1/deals/${dealId}/diligence/cancel`)
  },

  // ============================================
  // Embeddings Processing
  // ============================================

  /**
   * Trigger embedding generation for deal documents
   * This is required before chat can work (embeddings must be generated from markdown)
   */
  processEmbeddings: async (dealId: string): Promise<EmbeddingProcessResponse> => {
    return apiClient.post<EmbeddingProcessResponse>(`/api/v1/deals/${dealId}/embeddings`)
  },

  // ============================================
  // Deal Contacts
  // ============================================

  /**
   * Get all contacts associated with a deal
   */
  getContacts: async (dealId: string): Promise<DealContactListResponse> => {
    return apiClient.get<DealContactListResponse>(`/api/v1/deals/${dealId}/contacts`)
  },

  /**
   * Add a contact to a deal
   */
  addContact: async (dealId: string, data: DealContactCreateRequest): Promise<DealContact> => {
    return apiClient.post<DealContact>(`/api/v1/deals/${dealId}/contacts`, data)
  },

  /**
   * Update a contact's relationship with a deal
   */
  updateContact: async (
    dealId: string,
    contactId: string,
    data: DealContactUpdateRequest
  ): Promise<DealContact> => {
    return apiClient.put<DealContact>(`/api/v1/deals/${dealId}/contacts/${contactId}`, data)
  },

  /**
   * Remove a contact from a deal
   */
  removeContact: async (dealId: string, contactId: string): Promise<void> => {
    return apiClient.delete(`/api/v1/deals/${dealId}/contacts/${contactId}`)
  },

  // ============================================
  // Deal Knowledge (Documents, Links, Notes)
  // ============================================

  /**
   * Get all knowledge items for a deal (documents, links, notes)
   */
  getKnowledge: async (
    dealId: string,
    params?: { type?: string; limit?: number; offset?: number }
  ): Promise<DealKnowledgeResponse> => {
    return apiClient.get<DealKnowledgeResponse>(`/api/v1/deals/${dealId}/knowledge`, { params })
  },

  /**
   * Upload a document to a deal's knowledge base
   * Uses unified /knowledge endpoint with multipart form-data
   */
  uploadDocument: async (
    dealId: string,
    file: File,
    documentType: string = 'other'
  ): Promise<DealKnowledgeItem> => {
    const formData = new FormData()
    formData.append('type', 'document')
    formData.append('title', file.name)
    formData.append('file', file)
    formData.append('document_type', documentType)

    return apiClient.uploadFile<DealKnowledgeItem>(
      `/api/v1/deals/${dealId}/knowledge`,
      formData
    )
  },

  /**
   * Add a link to a deal's knowledge base
   * Uses unified /knowledge endpoint with multipart form-data
   */
  addLink: async (dealId: string, data: AddLinkRequest): Promise<DealKnowledgeItem> => {
    const formData = new FormData()
    formData.append('type', 'link')
    formData.append('title', data.title || data.url)
    formData.append('url', data.url)
    if (data.title) {
      formData.append('description', data.title)
    }

    return apiClient.uploadFile<DealKnowledgeItem>(
      `/api/v1/deals/${dealId}/knowledge`,
      formData
    )
  },

  /**
   * Delete a knowledge item (document, link, or note)
   * Uses unified /knowledge/{id} endpoint
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  deleteKnowledgeItem: async (_dealId: string, itemId: string, _itemType: 'document' | 'link' | 'note'): Promise<void> => {
    // Unified endpoint - dealId and itemType no longer needed for routing
    return apiClient.delete(`/api/v1/knowledge/${itemId}`)
  },

  /**
   * Update a knowledge item's metadata (e.g., document_type)
   */
  updateKnowledgeItem: async (
    itemId: string,
    data: { title?: string; metadata?: Record<string, unknown> }
  ): Promise<void> => {
    return apiClient.put(`/api/v1/knowledge/${itemId}`, data)
  },

  /**
   * Retry markdown extraction for a single knowledge item
   * @param knowledgeId - Knowledge item ID
   */
  retryKnowledgeMarkdown: async (knowledgeId: string): Promise<void> => {
    await apiClient.post(`/api/v1/knowledge/${knowledgeId}/markdown`, { force: true })
  },

  // ============================================
  // Deal Events (Activity Timeline)
  // ============================================

  /**
   * Get all events for a deal (activity timeline)
   */
  getEvents: async (
    dealId: string,
    params?: { limit?: number; offset?: number }
  ): Promise<DealEventListResponse> => {
    return apiClient.get<DealEventListResponse>(`/api/v1/deals/${dealId}/events`, { params })
  },

  // ============================================
  // Investment Memo
  // ============================================

  /**
   * Get memo status and content for a deal
   */
  getMemo: async (dealId: string): Promise<GetMemoResponse> => {
    return apiClient.get<GetMemoResponse>(`/api/v1/deals/${dealId}/memo`)
  },

  /**
   * Generate investment memo for a deal
   * @param dealId - Deal ID
   * @param force - Force regeneration even if memo exists
   */
  generateMemo: async (dealId: string, force: boolean = false): Promise<GetMemoResponse> => {
    return apiClient.post<GetMemoResponse>(`/api/v1/deals/${dealId}/memo/generate?force=${force}`)
  },

  // ============================================
  // Deal Comments (Diligence Review)
  // ============================================

  /**
   * Get all comments for a deal's diligence
   * @param dealId - Deal ID
   * @param params - Optional filters (dimension, status, pagination)
   */
  getComments: async (
    dealId: string,
    params?: ListCommentsParams
  ): Promise<DealCommentListResponse> => {
    return apiClient.get<DealCommentListResponse>(
      `/api/v1/deals/${dealId}/diligence/comments`,
      { params }
    )
  },

  /**
   * Create a new comment on a deal's diligence
   * @param dealId - Deal ID
   * @param data - Comment data (text, type, location, mentions, assignment)
   */
  createComment: async (dealId: string, data: DealCommentCreate): Promise<DealComment> => {
    return apiClient.post<DealComment>(`/api/v1/deals/${dealId}/diligence/comments`, data)
  },

  /**
   * Update a comment (only by creator)
   * @param dealId - Deal ID
   * @param commentId - Comment ID to update
   * @param data - Fields to update
   */
  updateComment: async (
    dealId: string,
    commentId: string,
    data: DealCommentUpdate
  ): Promise<DealComment> => {
    return apiClient.patch<DealComment>(
      `/api/v1/deals/${dealId}/diligence/comments/${commentId}`,
      data
    )
  },

  /**
   * Update comment status (mark as addressed, won't fix, or reopen)
   * @param dealId - Deal ID
   * @param commentId - Comment ID
   * @param data - New status and optional version
   */
  updateCommentStatus: async (
    dealId: string,
    commentId: string,
    data: DealCommentStatusUpdate
  ): Promise<DealComment> => {
    return apiClient.patch<DealComment>(
      `/api/v1/deals/${dealId}/diligence/comments/${commentId}/status`,
      data
    )
  },

  /**
   * Delete a comment (soft delete, only by creator)
   * @param dealId - Deal ID
   * @param commentId - Comment ID to delete
   */
  deleteComment: async (dealId: string, commentId: string): Promise<void> => {
    return apiClient.delete(`/api/v1/deals/${dealId}/diligence/comments/${commentId}`)
  },

  // ============================================
  // Diligence Version History
  // ============================================

  /**
   * Get all diligence versions for a deal
   * Returns previous versions plus current version for timeline display
   * @param dealId - Deal ID
   */
  getVersions: async (dealId: string): Promise<DiligenceVersionListResponse> => {
    return apiClient.get<DiligenceVersionListResponse>(
      `/api/v1/deals/${dealId}/diligence/versions`
    )
  },

  /**
   * Get a specific diligence version by version number
   * @param dealId - Deal ID
   * @param version - Version number (1-indexed)
   */
  getVersion: async (dealId: string, version: number): Promise<DiligenceVersionSnapshot> => {
    return apiClient.get<DiligenceVersionSnapshot>(
      `/api/v1/deals/${dealId}/diligence/versions/${version}`
    )
  },
}
